/**
 * Patient age helpers and age-transition banners (Phase 6, PAT-10 / PAT-03 / DSC-09).
 *
 * - "Turns 18 on <date>" when the 18th birthday is 0–30 days away (consent moves to the patient).
 * - "Turns 21 on <date>" when the 21st birthday is 0–90 days away (pediatric PDN eligibility ends).
 *
 * Pure functions: `today` is a parameter so the boundaries are testable. Dates are compared as
 * whole local calendar days. A Feb 29 birthday is observed on Mar 1 in non-leap years.
 */

export type AgeBannerKind = 'TURNS_18' | 'TURNS_21';

export interface AgeBanner {
  kind: AgeBannerKind;
  /** Age the patient is about to reach */
  age: 18 | 21;
  /** Birthday as YYYY-MM-DD */
  date: string;
  /** Whole days from today to the birthday (0 = today) */
  daysUntil: number;
  /** Display text, e.g. "Turns 18 on 03/01/2026" */
  message: string;
}

const BANNER_RULES: ReadonlyArray<{ kind: AgeBannerKind; age: 18 | 21; windowDays: number }> = [
  { kind: 'TURNS_18', age: 18, windowDays: 30 },
  { kind: 'TURNS_21', age: 21, windowDays: 90 },
];

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Parse 'YYYY-MM-DD' (optionally followed by a time) into its local calendar parts. */
export function parseDateParts(value: string | null | undefined): { year: number; month: number; day: number } | null {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  return { year, month, day };
}

/** Local midnight of a calendar day. Day overflow rolls forward (Feb 29 → Mar 1 in non-leap years). */
const localDay = (year: number, month: number, day: number): Date => new Date(year, month - 1, day);

const startOfDay = (date: Date): Date => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const wholeDaysBetween = (from: Date, to: Date): number =>
  Math.round((startOfDay(to).getTime() - startOfDay(from).getTime()) / MS_PER_DAY);

const toIso = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

const toUsDate = (date: Date): string =>
  `${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}/${date.getFullYear()}`;

/** The date a patient born on `dob` reaches `age` (Feb 29 → Mar 1 in non-leap years). */
export function birthdayAtAge(dob: string, age: number): Date | null {
  const parts = parseDateParts(dob);
  if (!parts) return null;
  return localDay(parts.year + age, parts.month, parts.day);
}

/** Completed years of age on `today`. Null when the DOB can't be parsed. */
export function ageInYears(dob: string | null | undefined, today: Date = new Date()): number | null {
  const parts = parseDateParts(dob);
  if (!parts) return null;
  let age = today.getFullYear() - parts.year;
  const birthdayThisYear = localDay(today.getFullYear(), parts.month, parts.day);
  if (startOfDay(today).getTime() < birthdayThisYear.getTime()) age -= 1;
  return age < 0 ? 0 : age;
}

/** Age banners to show for a patient today (empty when none apply). */
export function getPatientAgeBanners(dob: string | null | undefined, today: Date = new Date()): AgeBanner[] {
  if (!dob) return [];
  const banners: AgeBanner[] = [];
  for (const rule of BANNER_RULES) {
    const birthday = birthdayAtAge(dob, rule.age);
    if (!birthday) return [];
    const daysUntil = wholeDaysBetween(today, birthday);
    if (daysUntil >= 0 && daysUntil <= rule.windowDays) {
      banners.push({
        kind: rule.kind,
        age: rule.age,
        date: toIso(birthday),
        daysUntil,
        message: `Turns ${rule.age} on ${toUsDate(birthday)}`,
      });
    }
  }
  return banners;
}

export default getPatientAgeBanners;
